import type { ComponentChildren } from 'preact'
import { useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import {
  DateField,
  NumericField,
  RadioGroup,
  SelectField,
  TextField,
} from '~/components/primitives/Field'
import type { AthleteProfileInput, AthleteProfileRecord } from '~/models/athleteProfile'
import type { GoalInput, GoalRecord } from '~/models/goal'
import { PHASE_LABELS, type PhaseInput, type PhaseRecord } from '~/models/phase'
import type {
  DisplayPreferencesInput,
  LengthDisplayUnit,
  SettingsRecord,
  WeightDisplayUnit,
} from '~/models/settings'
import { WEEK_START_DAY_LABELS } from '~/models/settings'
import { describeWeekStartDay } from '~/services/weekBoundaryService'
import { calendarDateFrom, unwrapDate } from '~/types/dates'
import type { Inches, Pounds } from '~/types/units'
import { inchesToCentimetres, poundsToKilograms, unwrapNumber } from '~/types/units'
import {
  formatCalendarDate,
  formatLength,
  formatLengthValue,
  formatWeight,
  formatWeightValue,
} from '~/utils/format'
import styles from './SetupSections.module.css'

/**
 * Editing what setup collected.
 *
 * Onboarding wrote profile, phase and goal once and left no way back in. These
 * are that way back, and nothing more. A full reset is a separate control with
 * its own confirmation (`ResetSection`, D-067); **selective deletion and export
 * options are Checkpoint 4** and are absent rather than stubbed.
 *
 * Each section shows its current values and reveals a form only when asked, so
 * the screen stays readable on a phone and an accidental tap cannot change a
 * stored value.
 *
 * Entry is in the user's display units and converted to canonical kilograms and
 * centimetres before anything is saved (D-017, D-041), matching what the Today
 * screen already does with weight.
 */

/** A save returns an error message, or nothing when it succeeded. */
type SaveHandler<T> = (input: T) => Promise<string | undefined>

interface EditableSectionProps {
  heading: string
  editLabel: string
  summary: ComponentChildren
  form: ComponentChildren
  editing: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  busy: boolean
  error: string | undefined
  note?: ComponentChildren
}

function EditableSection({
  heading,
  editLabel,
  summary,
  form,
  editing,
  onEdit,
  onCancel,
  onSave,
  busy,
  error,
  note,
}: EditableSectionProps) {
  const headingId = `section-${heading.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} class={styles.sectionHeading}>
        {heading}
      </h2>

      {editing ? (
        <form
          class={styles.form}
          onSubmit={(event) => {
            event.preventDefault()
            onSave()
          }}
        >
          {form}
          {/* The note explains what is deliberately absent — an unimplemented
              phase type, a range Project Sola will not invent — so it belongs
              in the form too. Someone hunting for a missing control is looking
              at the form, not at the summary they just left. */}
          {note}
          {error === undefined ? null : (
            <p class={styles.problem} role="alert">
              {error}
            </p>
          )}
          <div class={styles.actions}>
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" busy={busy}>
              Save
            </Button>
          </div>
        </form>
      ) : (
        <>
          <dl class={styles.detail}>{summary}</dl>
          {note}
          <Button variant="secondary" onClick={onEdit} label={editLabel} fullWidth>
            Edit
          </Button>
        </>
      )}
    </section>
  )
}

function Row({ term, children }: { term: string; children: ComponentChildren }) {
  return (
    <div class={styles.row}>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  )
}

/** Blank means "no value", which is a legitimate edit for an optional field. */
function optionalNumber(raw: string): number | undefined {
  const trimmed = raw.trim()
  return trimmed === '' ? undefined : Number(trimmed)
}

function toCanonicalWeight(raw: string, unit: WeightDisplayUnit): number | undefined {
  const parsed = optionalNumber(raw)
  if (parsed === undefined) return undefined
  return unit === 'kg' ? parsed : unwrapNumber(poundsToKilograms(parsed as Pounds))
}

function toCanonicalLength(raw: string, unit: LengthDisplayUnit): number | undefined {
  const parsed = optionalNumber(raw)
  if (parsed === undefined) return undefined
  return unit === 'cm' ? parsed : unwrapNumber(inchesToCentimetres(parsed as Inches))
}

/* ------------------------------------------------------------------ profile */

interface ProfileSectionProps {
  profile: AthleteProfileRecord
  lengthUnit: LengthDisplayUnit
  onSave: SaveHandler<AthleteProfileInput>
}

export function ProfileSection({ profile, lengthUnit, onSave }: ProfileSectionProps) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const [height, setHeight] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [trainingExperience, setTrainingExperience] = useState('')
  const [primaryGoal, setPrimaryGoal] = useState('')
  const [preferredStyle, setPreferredStyle] = useState('')
  const [constraints, setConstraints] = useState('')

  function begin() {
    setHeight(formatLengthValue(profile.heightCm, lengthUnit))
    setDateOfBirth(unwrapDate(profile.dateOfBirth))
    setTrainingExperience(profile.trainingExperience ?? '')
    setPrimaryGoal(profile.primaryGoal ?? '')
    setPreferredStyle(profile.preferredStyle ?? '')
    setConstraints(profile.constraints ?? '')
    setError(undefined)
    setEditing(true)
  }

  async function save() {
    setBusy(true)

    // Clearing an optional field is a real edit, so blank means absent rather
    // than an empty string sitting in the record and exporting as "recorded".
    const experience = trainingExperience.trim()
    const goal = primaryGoal.trim()
    const style = preferredStyle.trim()
    const limits = constraints.trim()

    const message = await onSave({
      heightCm: toCanonicalLength(height, lengthUnit) ?? Number.NaN,
      dateOfBirth,
      avoidMultipleWeeklyChanges: profile.avoidMultipleWeeklyChanges,
      ...(experience === '' ? {} : { trainingExperience: experience }),
      ...(goal === '' ? {} : { primaryGoal: goal }),
      ...(style === '' ? {} : { preferredStyle: style }),
      ...(limits === '' ? {} : { constraints: limits }),
    })
    setBusy(false)
    if (message !== undefined) {
      setError(message)
      return
    }
    setEditing(false)
  }

  return (
    <EditableSection
      heading="About you"
      editLabel="Edit your height and date of birth"
      editing={editing}
      onEdit={begin}
      onCancel={() => setEditing(false)}
      onSave={() => void save()}
      busy={busy}
      error={error}
      note={
        <p class={styles.note}>
          Height feeds the {'Navy circumference estimate for men'}. Changing it affects future
          estimates only — each saved measurement keeps the height its own estimate was calculated
          from.
        </p>
      }
      summary={
        <>
          <Row term="Height">{formatLength(profile.heightCm, lengthUnit)}</Row>
          <Row term="Date of birth">{formatCalendarDate(profile.dateOfBirth)}</Row>
          <Row term="Training experience">{profile.trainingExperience ?? 'Not recorded'}</Row>
          <Row term="Long-term goal">{profile.primaryGoal ?? 'Not recorded'}</Row>
        </>
      }
      form={
        <>
          <NumericField
            label="Height"
            suffix={lengthUnit}
            value={height}
            onInput={setHeight}
            hint={`Entered in ${lengthUnit === 'cm' ? 'centimetres' : 'inches'}, following your display setting.`}
          />
          <DateField label="Date of birth" value={dateOfBirth} onInput={setDateOfBirth} />
          <TextField
            label="Training experience"
            value={trainingExperience}
            onInput={setTrainingExperience}
            placeholder="Three years of consistent lifting"
            hint="Optional. Included in an AI-review export as context."
          />
          <TextField
            label="Long-term physique goal"
            value={primaryGoal}
            onInput={setPrimaryGoal}
            placeholder="Lean and athletic, not bulky"
            hint="Optional."
          />
          <TextField
            label="Preferred approach"
            value={preferredStyle}
            onInput={setPreferredStyle}
            placeholder="Sustainable, minimal changes"
            hint="Optional."
          />
          <TextField
            label="Constraints worth knowing"
            value={constraints}
            onInput={setConstraints}
            multiline
            placeholder="Old shoulder injury; travel most months"
            hint="Optional."
          />
        </>
      }
    />
  )
}

/* -------------------------------------------------------------------- phase */

interface PhaseSectionProps {
  phase: PhaseRecord
  onSave: SaveHandler<Omit<PhaseInput, 'type'>>
}

export function PhaseSection({ phase, onSave }: PhaseSectionProps) {
  /* Whether the phase has begun, for the summary row's wording (D-107).
     A display-only comparison against the clock — it gates no behaviour, which
     is why it does not go through `getProjectStartState`; that accessor answers
     a question about the project, and this row is about the phase. */
  const phaseStarted = unwrapDate(phase.startDate) <= unwrapDate(calendarDateFrom(new Date()))
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const [startDate, setStartDate] = useState('')
  const [plannedEndDate, setPlannedEndDate] = useState('')
  const [minPercent, setMinPercent] = useState('')
  const [maxPercent, setMaxPercent] = useState('')
  const [notes, setNotes] = useState('')

  function begin() {
    setStartDate(unwrapDate(phase.startDate))
    setPlannedEndDate(unwrapDate(phase.plannedEndDate))
    setMinPercent(String(phase.targetWeeklyChangeMinPercent))
    setMaxPercent(String(phase.targetWeeklyChangeMaxPercent))
    setNotes(phase.notes ?? '')
    setError(undefined)
    setEditing(true)
  }

  async function save() {
    setBusy(true)
    const message = await onSave({
      startDate,
      plannedEndDate,
      targetWeeklyChangeMinPercent: Number(minPercent),
      targetWeeklyChangeMaxPercent: Number(maxPercent),
      ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
    })
    setBusy(false)
    if (message !== undefined) {
      setError(message)
      return
    }
    setEditing(false)
  }

  return (
    <EditableSection
      heading="Current phase"
      editLabel="Edit your current phase dates and target range"
      editing={editing}
      onEdit={begin}
      onCancel={() => setEditing(false)}
      onSave={() => void save()}
      busy={busy}
      error={error}
      note={
        <p class={styles.note}>
          The phase type is not editable here. Only phases whose rules exist can be selected, and
          only fat loss does so far. Past phases and the record of moving between them cannot be
          edited yet either.
        </p>
      }
      summary={
        <>
          <Row term="Type">{PHASE_LABELS[phase.type]}</Row>
          {/* "Starts" while the day is still ahead, the same correction the
              project row carries (D-107). Found by the test written for that
              row: a phase belonging to a project that has not begun has not
              begun either, and this said otherwise. Derived from the phase's own
              date rather than from the project's — they are kept in step by
              `rescheduleProjectStart`, and reading the project here would make
              this row depend on a record it does not hold. */}
          <Row term={phaseStarted ? 'Started' : 'Starts'}>
            {formatCalendarDate(phase.startDate)}
          </Row>
          <Row term="Planned end">{formatCalendarDate(phase.plannedEndDate)}</Row>
          <Row term="Target weekly change">
            {phase.targetWeeklyChangeMinPercent}% to {phase.targetWeeklyChangeMaxPercent}%
          </Row>
        </>
      }
      form={
        <>
          <DateField label="Start date" value={startDate} onInput={setStartDate} />
          <DateField
            label="Planned end date"
            value={plannedEndDate}
            onInput={setPlannedEndDate}
            hint="Moving this asks you to decide again when the new date arrives."
          />
          <NumericField
            label="Target weekly change, minimum"
            suffix="%"
            value={minPercent}
            onInput={setMinPercent}
            hint="A percentage of body weight per week. Negative for loss, positive for gain."
          />
          <NumericField
            label="Target weekly change, maximum"
            suffix="%"
            value={maxPercent}
            onInput={setMaxPercent}
          />
          <TextField label="Notes" value={notes} onInput={setNotes} multiline hint="Optional." />
        </>
      }
    />
  )
}

/**
 * A range with either end optional.
 *
 * One bound alone is a usable target — "no heavier than 78 kg" says something
 * — so it reads as such rather than being shown as half of a broken range.
 */
function describeRange(min: string | undefined, max: string | undefined): string {
  if (min !== undefined && max !== undefined) return `${min} to ${max}`
  if (min !== undefined) return `From ${min}`
  if (max !== undefined) return `Up to ${max}`
  return 'Not set'
}

/* --------------------------------------------------------------------- goal */

interface GoalSectionProps {
  goal: GoalRecord
  weightUnit: WeightDisplayUnit
  lengthUnit: LengthDisplayUnit
  onSave: SaveHandler<GoalInput>
}

export function GoalSection({ goal, weightUnit, lengthUnit, onSave }: GoalSectionProps) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const [targetDate, setTargetDate] = useState('')
  const [weightMin, setWeightMin] = useState('')
  const [weightMax, setWeightMax] = useState('')
  const [waistMin, setWaistMin] = useState('')
  const [waistMax, setWaistMax] = useState('')
  const [notes, setNotes] = useState('')

  function begin() {
    setTargetDate(unwrapDate(goal.targetDate))
    setWeightMin(
      goal.targetWeightMinKg === undefined
        ? ''
        : formatWeightValue(goal.targetWeightMinKg, weightUnit),
    )
    setWeightMax(
      goal.targetWeightMaxKg === undefined
        ? ''
        : formatWeightValue(goal.targetWeightMaxKg, weightUnit),
    )
    setWaistMin(
      goal.targetWaistMinCm === undefined
        ? ''
        : formatLengthValue(goal.targetWaistMinCm, lengthUnit),
    )
    setWaistMax(
      goal.targetWaistMaxCm === undefined
        ? ''
        : formatLengthValue(goal.targetWaistMaxCm, lengthUnit),
    )
    setNotes(goal.notes ?? '')
    setError(undefined)
    setEditing(true)
  }

  async function save() {
    setBusy(true)
    const weightMinKg = toCanonicalWeight(weightMin, weightUnit)
    const weightMaxKg = toCanonicalWeight(weightMax, weightUnit)
    const waistMinCm = toCanonicalLength(waistMin, lengthUnit)
    const waistMaxCm = toCanonicalLength(waistMax, lengthUnit)

    const message = await onSave({
      startDate: unwrapDate(goal.startDate),
      targetDate,
      ...(weightMinKg === undefined ? {} : { targetWeightMinKg: weightMinKg }),
      ...(weightMaxKg === undefined ? {} : { targetWeightMaxKg: weightMaxKg }),
      ...(waistMinCm === undefined ? {} : { targetWaistMinCm: waistMinCm }),
      ...(waistMaxCm === undefined ? {} : { targetWaistMaxCm: waistMaxCm }),
      ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
    })
    setBusy(false)
    if (message !== undefined) {
      setError(message)
      return
    }
    setEditing(false)
  }

  const weightRange = describeRange(
    goal.targetWeightMinKg === undefined
      ? undefined
      : formatWeight(goal.targetWeightMinKg, weightUnit),
    goal.targetWeightMaxKg === undefined
      ? undefined
      : formatWeight(goal.targetWeightMaxKg, weightUnit),
  )
  const waistRange = describeRange(
    goal.targetWaistMinCm === undefined
      ? undefined
      : formatLength(goal.targetWaistMinCm, lengthUnit),
    goal.targetWaistMaxCm === undefined
      ? undefined
      : formatLength(goal.targetWaistMaxCm, lengthUnit),
  )

  return (
    <EditableSection
      heading="Goal"
      editLabel="Edit your goal target date and ranges"
      editing={editing}
      onEdit={begin}
      onCancel={() => setEditing(false)}
      onSave={() => void save()}
      busy={busy}
      error={error}
      note={
        <p class={styles.note}>
          Target ranges are optional and yours to choose. A goal weight or waist is personal, so
          Project Sola does not suggest one. Leave a box empty for no target.
        </p>
      }
      summary={
        <>
          <Row term="Target date">{formatCalendarDate(goal.targetDate)}</Row>
          <Row term="Target weight">{weightRange}</Row>
          <Row term="Target waist">{waistRange}</Row>
        </>
      }
      form={
        <>
          <DateField
            label="Target date"
            value={targetDate}
            onInput={setTargetDate}
            hint="The date your long-term goal is aimed at, usually months beyond the current phase."
          />
          <NumericField
            label="Target weight, lower end"
            suffix={weightUnit}
            value={weightMin}
            onInput={setWeightMin}
            hint="Optional. Leave blank for no target."
          />
          <NumericField
            label="Target weight, upper end"
            suffix={weightUnit}
            value={weightMax}
            onInput={setWeightMax}
          />
          <NumericField
            label="Target waist, lower end"
            suffix={lengthUnit}
            value={waistMin}
            onInput={setWaistMin}
            hint="Optional."
          />
          <NumericField
            label="Target waist, upper end"
            suffix={lengthUnit}
            value={waistMax}
            onInput={setWaistMax}
          />
          <TextField label="Notes" value={notes} onInput={setNotes} multiline hint="Optional." />
        </>
      }
    />
  )
}

/* -------------------------------------------------------------- preferences */

interface PreferencesSectionProps {
  settings: SettingsRecord
  onSave: SaveHandler<DisplayPreferencesInput>
}

const WEEK_START_OPTIONS = (Object.keys(WEEK_START_DAY_LABELS) as `${0 | 1 | 2 | 3 | 4 | 5 | 6}`[])
  .map((key) => Number(key))
  .sort((left, right) => left - right)
  .map((day) => ({
    value: String(day),
    label: WEEK_START_DAY_LABELS[day as keyof typeof WEEK_START_DAY_LABELS],
  }))

export function PreferencesSection({ settings, onSave }: PreferencesSectionProps) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const [weightUnit, setWeightUnit] = useState<WeightDisplayUnit>('kg')
  const [lengthUnit, setLengthUnit] = useState<LengthDisplayUnit>('cm')
  const [weekStart, setWeekStart] = useState('1')
  const [threshold, setThreshold] = useState('')

  function begin() {
    setWeightUnit(settings.weightDisplayUnit)
    setLengthUnit(settings.lengthDisplayUnit)
    setWeekStart(String(settings.weekStartDay))
    setThreshold(String(unwrapNumber(settings.circumferenceSpreadThresholdCm)))
    setError(undefined)
    setEditing(true)
  }

  async function save() {
    setBusy(true)
    const message = await onSave({
      weightDisplayUnit: weightUnit,
      lengthDisplayUnit: lengthUnit,
      weekStartDay: Number(weekStart),
      circumferenceSpreadThresholdCm: Number(threshold),
    })
    setBusy(false)
    if (message !== undefined) {
      setError(message)
      return
    }
    setEditing(false)
  }

  return (
    <EditableSection
      heading="Display and measurement"
      editLabel="Edit display units and the measurement spread warning"
      editing={editing}
      onEdit={begin}
      onCancel={() => setEditing(false)}
      onSave={() => void save()}
      busy={busy}
      error={error}
      note={
        <p class={styles.note}>
          Units change what you see and type. Project Sola always stores weight in kilograms and
          measurements in centimetres, so switching never alters a saved value.
        </p>
      }
      summary={
        <>
          <Row term="Weight shown in">
            {settings.weightDisplayUnit === 'kg' ? 'Kilograms' : 'Pounds'}
          </Row>
          <Row term="Lengths shown in">
            {settings.lengthDisplayUnit === 'cm' ? 'Centimetres' : 'Inches'}
          </Row>
          <Row term="Weeks start on">
            {describeWeekStartDay(
              settings,
              calendarDateFrom(new Date()),
              formatCalendarDate,
              WEEK_START_DAY_LABELS,
            )}
          </Row>
          <Row term="Warn when readings vary by">
            {unwrapNumber(settings.circumferenceSpreadThresholdCm)} cm
          </Row>
        </>
      }
      form={
        <>
          <RadioGroup
            legend="Show weight in"
            value={weightUnit}
            onChange={setWeightUnit}
            options={[
              { value: 'kg' as WeightDisplayUnit, label: 'Kilograms' },
              { value: 'lb' as WeightDisplayUnit, label: 'Pounds' },
            ]}
          />
          <RadioGroup
            legend="Show lengths in"
            value={lengthUnit}
            onChange={setLengthUnit}
            options={[
              { value: 'cm' as LengthDisplayUnit, label: 'Centimetres' },
              { value: 'in' as LengthDisplayUnit, label: 'Inches' },
            ]}
          />
          <SelectField
            label="Weeks start on"
            value={weekStart}
            options={WEEK_START_OPTIONS}
            onChange={setWeekStart}
            /* D-105 §8: a week already running is authoritative for its whole
               span, so this cannot take effect today. The control says so before
               it is used rather than the change appearing not to work. */
            hint="Used for weekly trends and the review cadence. A change takes effect when the current week ends."
          />
          <NumericField
            label="Warn when three readings vary by more than"
            suffix="cm"
            value={threshold}
            onInput={setThreshold}
            hint="Always in centimetres, because it is a tolerance rather than a measurement. The default is 0.7 cm."
          />
        </>
      }
    />
  )
}
